import { useState } from "react"
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { ArrowLeft, ArrowRight, Check, MessageCircle, Send, Users } from "lucide-react"
import { InstagramIcon } from "@/components/brand/instagram-icon"
import { toast } from "sonner"
import { z } from "zod"
import type { AutomationDetail } from "@vienna/contracts"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { TagInput } from "@/components/ui/tag-input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import { accountOptions } from "@/components/brand/account-options"
import { AutoHeading, Choice, Note } from "@/components/automations/primitives"
import { api, ApiError } from "@/lib/api"
import { channelsQuery, templatesQuery, workspaceQuery } from "@/lib/queries"
import { useWorkspaceContext } from "@/lib/session"

const GUIDE_MESSAGE = "Hi {{first_name | there}}, here’s your studio guide. What would you like to create?"

export const Route = createFileRoute("/w/$workspace/automations/new")({
  validateSearch: z.object({ template: z.string().optional() }),
  beforeLoad: async ({ context, params }) => {
    const ctx = await context.queryClient.ensureQueryData(workspaceQuery(params.workspace))
    if (!ctx.permissions.includes("edit_automations")) throw redirect({ to: "/w/$workspace/automations", params })
  },
  loader: ({ context, params }) => Promise.all([context.queryClient.ensureQueryData(templatesQuery(params.workspace)), context.queryClient.ensureQueryData(channelsQuery(params.workspace))]),
  component: TemplateSetup,
})

/** Screen 17. Creates draft v1 from the template with the setup fields applied. */
function TemplateSetup() {
  const { workspace } = useWorkspaceContext()
  const navigate = useNavigate()
  const { template: templateKey = "comment_to_dm" } = Route.useSearch()
  const templates = useSuspenseQuery(templatesQuery(workspace.slug)).data
  const channels = useSuspenseQuery(channelsQuery(workspace.slug)).data.connections.filter((c) => c.status !== "disconnected")
  const template = templates.find((t) => t.key === templateKey) ?? templates.find((t) => t.key === "blank")!
  const [name, setName] = useState(template.key === "comment_to_dm" ? "Studio guide" : template.key === "blank" ? "Untitled automation" : template.name)
  const [connectionId, setConnectionId] = useState(channels[0]?.id ?? "")
  const [keyword, setKeyword] = useState("GUIDE")
  const [scope, setScope] = useState<"all_posts" | "selected_posts">("all_posts")
  const [postIds, setPostIds] = useState<string[]>([])
  const [message, setMessage] = useState(GUIDE_MESSAGE)
  const [url, setUrl] = useState("https://example.com/studio-guide")
  const params = { workspace: workspace.slug }
  const create = useMutation({
    mutationFn: () => api<AutomationDetail>(`/w/${workspace.slug}/automations`, { method: "POST", body: JSON.stringify({ name: name.trim(), templateKey: template.key, ...(connectionId ? { connectionId } : {}), setup: { keyword, scope, postIds, message, buttonLabel: "Open studio guide", buttonUrl: url } }) }),
    onSuccess: async (created) => {
      toast.success("Draft created")
      await navigate({ to: "/w/$workspace/automations/$automationId", params: { ...params, automationId: created.id } })
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.problem.title : "Could not create the draft"),
  })
  const connection = channels.find((c) => c.id === connectionId)
  const isComment = template.key === "comment_to_dm"
  return (
    <div className="page-wrap auto-page">
      <AutoHeading
        title="Make it yours"
        description={`${template.name} · ${connection ? (connection.provider === "instagram" ? "Instagram" : "Messenger") : "Choose a channel"}`}
        back={
          <Button variant="ghost" size="sm" className="mb-3 -ml-2" asChild>
            <Link to="/w/$workspace/automations" params={params}>
              <ArrowLeft />
              Automations
            </Link>
          </Button>
        }
      >
        <Button variant="outline" asChild>
          <Link to="/w/$workspace/automations/templates" params={params}>
            Change template
          </Link>
        </Button>
      </AutoHeading>
      <div className="auto-two-col">
        <Card>
          <CardHeader>
            <CardTitle>Set up your automation</CardTitle>
            <CardDescription>A few details, then your canvas is ready.</CardDescription>
          </CardHeader>
          <CardContent>
            <form
              className="space-y-6"
              onSubmit={(e) => {
                e.preventDefault()
                create.mutate()
              }}
            >
              <div className="space-y-2">
                <Label htmlFor="flow-name">Automation name</Label>
                <Input id="flow-name" required autoFocus maxLength={80} value={name} onFocus={(e) => e.target.select()} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Connected account</Label>
                {channels.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No channel connected yet.{" "}
                    <Link className="underline" to="/w/$workspace/channels/new" params={params} search={{}}>
                      Connect one
                    </Link>{" "}
                    to publish this journey.
                  </p>
                ) : (
                  <div className="flex items-center gap-3 rounded-lg border p-3">
                    {connection?.provider === "messenger" ? <MessageCircle className="size-5" /> : <InstagramIcon className="size-5" />}
                    <div className="flex-1">
                      <Choice label="Connected account" value={connectionId} onChange={setConnectionId} values={accountOptions(channels, "messages")} className="w-full border-0 px-0 shadow-none" />
                      <p className="text-xs text-muted-foreground">{connection?.provider === "instagram" ? "Instagram professional account" : "Facebook Page"}</p>
                    </div>
                    <Badge variant="outline">
                      <Check className="size-3" />
                      {connection?.status === "connected" ? "Connected" : "Needs attention"}
                    </Badge>
                  </div>
                )}
              </div>
              {isComment && (
                <>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="flow-keyword">Comment keyword</Label>
                      <Input id="flow-keyword" required value={keyword} onChange={(e) => setKeyword(e.target.value)} />
                      <p className="text-xs text-muted-foreground">Whole word · case insensitive</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Where to listen</Label>
                      <Choice label="Post scope" value={scope} onChange={setScope} values={[{ value: "all_posts", label: "All new posts" }, { value: "selected_posts", label: "Selected posts" }]} className="w-full" />
                    </div>
                  </div>
                  {scope === "selected_posts" && (
                    <div className="space-y-2">
                      <Label htmlFor="flow-posts">Post ids</Label>
                      <TagInput id="flow-posts" placeholder="Type a provider media id and press Enter" value={postIds} onChange={setPostIds} />
                    </div>
                  )}
                  <Separator />
                  <div className="space-y-2">
                    <Label htmlFor="setup-message">Private message</Label>
                    <Textarea id="setup-message" rows={4} value={message} onChange={(e) => setMessage(e.target.value)} />
                    <p className="text-xs text-muted-foreground">First names use “there” when unavailable.</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="guide-url">Guide destination</Label>
                    <Input id="guide-url" type="url" value={url} onChange={(e) => setUrl(e.target.value)} required />
                  </div>
                </>
              )}
              <div className="rounded-lg bg-muted/60 p-3 text-xs leading-5">
                <strong className="block font-medium">Once per contact, across selected posts</strong>A new version keeps the same entry history. Non-matching comments remain in the Support inbox.
              </div>
              <Button type="submit" className="w-full" disabled={create.isPending || !name.trim()}>
                Create draft
                <ArrowRight />
              </Button>
            </form>
          </CardContent>
        </Card>
        <div>
          <Card className="bg-muted/25">
            <CardHeader>
              <Badge variant="outline" className="mb-3 w-fit">
                Customer preview
              </Badge>
              <CardTitle>One comment. A real conversation.</CardTitle>
              <CardDescription>This is a preview. No messages are sent.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="auto-journey">
                {(isComment
                  ? [
                      [MessageCircle, "They leave a comment", `${keyword || "GUIDE"} please!`],
                      [MessageCircle, "You acknowledge it publicly", "Thanks! Check your messages for the guide."],
                      [Send, "They receive a private message", message.replace(/\{\{\s*first_name[^}]*\}\}/g, "Maya")],
                      [Users, "Your team continues", "A reply assigns the conversation to Support and pauses automation."],
                    ]
                  : [
                      [MessageCircle, "They start a conversation", "Hi! Is the studio open on Sunday?"],
                      [Send, "They receive a reply", template.description],
                      [Users, "Your team continues", "The conversation is assigned and automation pauses."],
                    ]
                ).map(([Icon, title, copy], i) => {
                  const NodeIcon = Icon as typeof MessageCircle
                  return (
                    <div className="auto-journey-step" key={i}>
                      <span>
                        <NodeIcon className="size-4" />
                      </span>
                      <div>
                        <p className="font-medium">{title as string}</p>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">{copy as string}</p>
                      </div>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>
          <Note>Messaging permission and eligibility are checked again before each send.</Note>
        </div>
      </div>
    </div>
  )
}
